import React, {useContext, useEffect, useRef, useState} from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import {Avatar, Checkbox, Chip, FormControlLabel, FormGroup, MenuItem, Select, Tooltip} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import {Description, DescriptionList, DescriptionTerm} from "../parts/Description.parts";
import {download, getOrganisations} from "../../utils/api";
import {UserContext} from "../auth/UserProvider";
import CloseIcon from "@mui/icons-material/Close";
import {FileDrop} from "react-file-drop";
import AttachmentIcon from "@mui/icons-material/Attachment";
import FileDownload from "js-file-download";

export default function AddIndicatorValueDialog(props) {
  const { indicator, entity, confirmationHandler, action = 'create' } = props;
  const [open, setOpen] = useState(false);
  const [indicatorValueDescription, setIndicatorValueDescription] = useState('');
  const [indicatorValueActivity, setIndicatorValueActivity] = useState(undefined);
  const [indicatorValueComment, setIndicatorValueComment] = useState('');
  const [organisation, setOrganisation] = useState(undefined);
  const [numericValue, setNumericValue] = useState(0);
  const [secondaryNumericValue, setSecondaryNumericValue] = useState(0);
  const [tertiaryNumericValue, setTertiaryNumericValue] = useState(0);
  const [availableOrganisations, setAvailableOrganisations] = React.useState([]);
  const [attachments, setAttachments] = useState([]);

  const authState = useContext(UserContext);

  const cancelRequest = useRef();

  const fileInputRef = useRef(null);

  const fetchData =
      async () => {
        try {
          const result = await getOrganisations();
          const orgData = result.data;

          if (!cancelRequest.current) {
            if (orgData && Object.keys(orgData).length > 0) {
              setOrganisation(orgData[0]);
              setAvailableOrganisations(orgData);
            }
          }
        } catch (error) {
        }
      };

  useEffect(() => {
    fetchData();
  }, []);

  const handleClickOpen = () => {
    setOpen(true);
    setIndicatorValueDescription(entity && entity.description ? entity.description : '');
    setIndicatorValueActivity(entity && entity.activity ? entity.activity : undefined);
    setIndicatorValueComment(entity && entity.comment ? entity.comment : '');
    setOrganisation(entity && entity.organisation ? entity.organisation : undefined);
    setNumericValue(entity && entity.numericValue ? entity.numericValue : 0);
    setSecondaryNumericValue(entity && entity.secondaryNumericValue ? entity.secondaryNumericValue : 0);
    setTertiaryNumericValue(entity && entity.tertiaryNumericValue ? entity.tertiaryNumericValue : 0);
    setAttachments(entity && entity.attachments ? entity.attachments : []);
  };

  const handleClose = () => {
    setOpen(false);
    clearFormData();
  };

  const handleConfirm = () => {
    action === 'create' ?
      confirmationHandler(
          indicator.id,
          indicatorValueDescription,
          indicatorValueActivity,
          indicatorValueComment,
          organisation ? organisation : availableOrganisations.filter(avOrg => avOrg.id === parseInt(authState.org.split('_')[1]))[0],
          numericValue,
          indicator && indicator.secondaryTargetValueNumeric ? secondaryNumericValue : null,
          indicator && indicator.tertiaryTargetValueNumeric ? tertiaryNumericValue : null,
          attachments) :
        confirmationHandler(
            entity.id,
            indicator.id,
            indicatorValueDescription,
            indicatorValueActivity,
            indicatorValueComment,
            organisation ? organisation : availableOrganisations.filter(avOrg => avOrg.id === parseInt(authState.org.split('_')[1]))[0],
            numericValue,
            indicator && indicator.secondaryTargetValueNumeric ? secondaryNumericValue : null,
            indicator && indicator.tertiaryTargetValueNumeric ? tertiaryNumericValue : null,
            attachments);
    handleClose();
  };

  const clearFormData = () => {
    setIndicatorValueDescription('');
    setIndicatorValueActivity(undefined);
    setIndicatorValueComment('');
    setOrganisation(undefined);
    setNumericValue(0);
    setSecondaryNumericValue(0);
    setTertiaryNumericValue(0);
    setAttachments([]);
  };

  const onIndicatorValueDescriptionChange = (e) => {
    setIndicatorValueDescription(e.target.value);
  };

  const onIndicatorValueCommentChange = (e) => {
    setIndicatorValueComment(e.target.value);
  };

  const onNumericValueChange = (e) => {
    setNumericValue(e.target.value);
  };

  const onSecondaryNumericValueChange = (e) => {
    setSecondaryNumericValue(e.target.value);
  };

  const onTertiaryNumericValueChange = (e) => {
    setTertiaryNumericValue(e.target.value);
  };

  const handleOrganisationChange = (event) => {
    setOrganisation(availableOrganisations.find(p => p.id === event.target.value));
  };

  const onTargetClick = () => {
    fileInputRef.current.click()
  }

  const onFileInputChange = (event) => {
    const { files } = event.target;
    setAttachments([
      ...attachments,
      ...files
    ]);
  }

  const handleDeleteAttachment = (fileToDelete) => {
    setAttachments(attachments.filter(file => file !== fileToDelete));
  };

  return (
      <div>
        {action === 'create' &&
          <Button variant="outlined" color="secondary" sx={{fontSize: 14}} onClick={handleClickOpen}>
            Add { indicator && indicator.targetValueNumeric === 1 ? 'milestone' : 'entry'}
          </Button>
        }
        { action === 'edit' &&
        <Tooltip title="Edit">
          <IconButton aria-label="edit"
                      onClick={handleClickOpen}>
            <EditIcon />
          </IconButton>
        </Tooltip>
        }
        <Dialog fullWidth open={open} onClose={handleClose} aria-labelledby="form-dialog-title">
          <DialogTitle id="form-dialog-title">{ action === 'create' ? 'Add' : 'Edit'} { indicator && indicator.targetValueNumeric === 1 ? 'milestone' : 'entry'}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Please add the necessary information
            </DialogContentText>
            <TextField
                autoFocus
                margin="normal"
                id="name"
                label="Description"
                type="text"
                value={indicatorValueDescription}
                onChange={onIndicatorValueDescriptionChange}
                fullWidth
                variant="outlined"
            />
            <TextField
                autoFocus={false}
                margin="normal"
                id="name"
                label="Comment"
                type="text"
                value={indicatorValueComment}
                onChange={onIndicatorValueCommentChange}
                fullWidth
                variant="outlined"
            />
            <DescriptionList>
              <DescriptionTerm>Organisation</DescriptionTerm>
              <Description>
                <Select
                    labelId="org-select"
                    id="org-select"
                    value={organisation ? organisation.id + '' : authState.org ? authState.org.split('_')[1] : '1'}
                    variant="outlined"
                    onChange={handleOrganisationChange}
                    disabled={authState.org}
                >
                  {availableOrganisations.length > 0 && availableOrganisations.filter(avOrg => avOrg.id === parseInt(authState.org.split('_')[1])).map((p) => (
                      <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                  ))}
                </Select>
              </Description>
            </DescriptionList>
            <br/>
            { indicator && indicator.targetValueNumeric === 1 &&
              <FormGroup>
                <FormControlLabel control={<Checkbox checked={numericValue !== 0} onChange={(event) => setNumericValue(event.target.checked ? 1 : 0)} />} label="Milestone completed" />
              </FormGroup>
            }
            { indicator && indicator.targetValueNumeric !== 1 &&
              <TextField
                  autoFocus={false}
                  margin="normal"
                  id="name"
                  label="Value"
                  value={numericValue}
                  onChange={onNumericValueChange}
                  type="number"
                  variant="outlined"
              />
            }
            { indicator && indicator.secondaryTargetValueNumeric &&
                <>
                  <br/>
                  <TextField
                      autoFocus={false}
                      margin="normal"
                      id="name"
                      label={`Secondary value (${indicator.secondaryTargetValueDescription})`}
                      value={secondaryNumericValue}
                      onChange={onSecondaryNumericValueChange}
                      type="number"
                      variant="outlined"
                  />
                </>
            }
            { indicator && indicator.tertiaryTargetValueNumeric &&
              <>
                <br/>
                <TextField
                    autoFocus={false}
                    margin="normal"
                    id="name"
                    label={`Tertiary value (${indicator.tertiaryTargetValueDescription})`}
                    value={tertiaryNumericValue}
                    onChange={onTertiaryNumericValueChange}
                    type="number"
                    variant="outlined"
                />
              </>
            }
            <br/>
            <input
                onChange={onFileInputChange}
                ref={fileInputRef}
                type="file"
                multiple="multiple"
                style={{display:'none'}}
            />
            { attachments && attachments.length > 0 && attachments.map(file => (
                <Chip
                    onClick={() => download(file.uuid).then((response) => {
                      FileDownload(response.data, file.filename);
                    })}
                    sx={{margin: 1}}
                    avatar={
                      <Avatar>
                        {file.name ? file.name.slice(file.name.lastIndexOf('.') + 1) : file.filename.slice(file.filename.lastIndexOf('.') + 1)}
                      </Avatar>}
                    label={file.name ? file.name : file.filename}
                    onDelete={() => handleDeleteAttachment(file)}
                    deleteIcon={<CloseIcon />}
                />
            ))}
            <div style={{ backgroundColor: '#EEEEEE', borderRadius: '5px', border: '1px solid #EEEEEE', width: 550, color: '#666666', padding: 20 }}>
              <FileDrop
                  onDrop={(files) => {
                    setAttachments([...attachments, ...files]);
                  }}
                  onTargetClick={onTargetClick}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  margin: '16px 16px',
                }}>
                  <AttachmentIcon sx={{ fontSize: 24, marginRight: 1 }} />
                  <span>Drop one or multiple documents here for documentation!</span>
                </div>

              </FileDrop>
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} color="primary" size="small">
              Cancel
            </Button>
            <Button onClick={handleConfirm} color="primary" size="small">
              { action === 'create' ? 'Add' : 'Edit'}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
  );
}